import {useState} from 'react';
import {AtSign, Check, Loader2, X} from 'lucide-react';
import {Button} from '@ui/shadcn/button';
import {HighlightedTextarea} from '@ui/local/HighlightedTextarea';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@ui/shadcn/popover';
import type {useTaskComments} from '../model/useTaskComments';
import {TASK_COMMENT_MAX_LENGTH} from '../model/mentions';

/** Компактный редактор; подсказки упоминаний открываются над полем вне потока карточки. */
export const TaskCommentEditor = ({
  form,
  onCancel,
  author,
}: {
  form: ReturnType<typeof useTaskComments>;
  onCancel?: () => void;
  author?: string;
}) => {
  const [mentionsRequested, setMentionsRequested] = useState(false);
  const mentionsOpen =
    mentionsRequested ||
    (form.mentionQuery !== null &&
      form.matchedManagers.length > 0 &&
      form.selectedMentions.length < 2);
  const closeMentions = () => {
    setMentionsRequested(false);
    form.closeMentions();
  };
  const submitDisabled =
    !form.commentDraft.trim() ||
    form.commentDraft.length > TASK_COMMENT_MAX_LENGTH ||
    !form.canSubmit ||
    form.isPending;
  const mentionButton = (
    <PopoverTrigger asChild>
      <Button
        type='button'
        variant='ghost'
        size='icon'
        className='size-6 text-[var(--color-muted-foreground)]'
        disabled={form.isPending || !form.canSubmit}
        aria-label='Упоминания участников'
        title='Упоминания участников'
        aria-expanded={mentionsOpen}
      >
        <AtSign size={13} />
      </Button>
    </PopoverTrigger>
  );
  return (
    <Popover
      open={mentionsOpen && !form.isPending}
      onOpenChange={(open) => {
        if (open) setMentionsRequested(true);
        else closeMentions();
      }}
    >
      <div className='space-y-2'>
        {onCancel && (
          <div className='flex items-center justify-between gap-2'>
            <span className='text-xs font-semibold'>{author}</span>
            <div className='flex items-center gap-0'>
              {mentionButton}
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-6 text-[var(--color-primary)]'
                disabled={submitDisabled}
                aria-label='Сохранить комментарий'
                title='Сохранить (Ctrl+Enter)'
                onClick={() => void form.sendComment()}
              >
                {form.isPending ? (
                  <Loader2 size={13} className='animate-spin' />
                ) : (
                  <Check size={13} />
                )}
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-6'
                disabled={form.isPending}
                aria-label='Отменить редактирование'
                title='Отменить редактирование'
                onClick={onCancel}
              >
                <X size={13} />
              </Button>
            </div>
          </div>
        )}
        <PopoverAnchor asChild>
          <div className='relative'>
            <HighlightedTextarea
              {...form.textareaProps}
              ranges={form.highlightRanges}
              aria-label={
                onCancel ? 'Редактирование комментария' : 'Новый комментарий'
              }
              autoFocus={Boolean(onCancel)}
              rows={2}
              placeholder='Добавьте комментарий… Используйте @ для упоминания участника'
              className={
                onCancel
                  ? 'w-full field-sizing-fixed h-16 min-h-16 resize-none text-xs md:text-xs leading-relaxed'
                  : 'w-full field-sizing-fixed h-20 min-h-20 max-h-40 resize-y pb-9 leading-relaxed'
              }
            />
            {!onCancel && (
              <div className='absolute bottom-1.5 right-2 flex items-center gap-1'>
                {mentionButton}
                <Button
                  type='button'
                  size='xs'
                  disabled={submitDisabled}
                  onClick={() => void form.sendComment()}
                >
                  {form.isPending ? 'Отправка…' : 'Отправить'}
                </Button>
              </div>
            )}
          </div>
        </PopoverAnchor>
        {!onCancel && (
          <p className='text-[11px] text-[var(--color-muted-foreground)]'>
            Ctrl+Enter — отправить · @ — упомянуть участника
          </p>
        )}
      </div>
      <PopoverContent
        side='top'
        align='start'
        sideOffset={6}
        avoidCollisions={false}
        style={{zIndex: 'calc(var(--z-modal-task) + 20)'}}
        className='w-[var(--radix-popover-trigger-width)] min-w-[240px] max-w-[calc(100vw-2rem)] max-h-56 overflow-y-auto p-1'
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onFocusOutside={(event) => {
          if (
            event.detail.originalEvent.target === form.textareaProps.ref.current
          )
            event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          event.stopPropagation();
          closeMentions();
        }}
      >
        <div className='flex items-center justify-between gap-2 px-2 py-1'>
          <p className='text-[11px] text-[var(--color-muted-foreground)]'>
            Упомянуть участника задачи
          </p>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='size-6 shrink-0'
            aria-label='Закрыть упоминания'
            title='Закрыть (Esc)'
            onClick={closeMentions}
          >
            <X className='size-3.5' />
          </Button>
        </div>
        {form.selectedMentions.map((person) => (
          <Button
            key={person.userSid}
            type='button'
            variant='ghost'
            size='sm'
            className='h-auto w-full justify-between px-2 py-1.5 text-xs text-[var(--color-primary)]'
            aria-label={`Убрать упоминание: ${person.displayName}`}
            onClick={() => form.removeMention(person.userSid)}
          >
            {person.displayName}
            <span className='flex size-6 shrink-0 items-center justify-center'>
              <X className='size-3.5' />
            </span>
          </Button>
        ))}
        {form.selectedMentions.length < 2 ? (
          form.matchedManagers.map((person) => (
            <Button
              key={person.userSid}
              type='button'
              variant='ghost'
              className='h-auto w-full justify-start text-left px-2 py-1.5 text-xs font-normal'
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                form.insertMention(person);
                closeMentions();
              }}
            >
              {person.displayName}
              <span className='text-[var(--color-muted-foreground)]'>
                · {person.userSid.slice(0, 8)}
              </span>
            </Button>
          ))
        ) : (
          <p className='px-2 py-1 text-[11px] text-[var(--color-muted-foreground)]'>
            Можно упомянуть не более двух участников
          </p>
        )}
        {form.matchedManagers.length === 0 &&
          form.selectedMentions.length === 0 && (
            <p className='px-2 py-1 text-xs text-[var(--color-muted-foreground)]'>
              Нет подходящих участников
            </p>
          )}
      </PopoverContent>
    </Popover>
  );
};
